// Page admin (#admin, choix de Léni du 5 novembre 2026) : se donner pièces, gemmes, jetons et coffres de la Forge,
// débloquer le pass, tous les cosmétiques et la Forge, relire le journal. Tout est vérifié par le serveur (403 pour un
// non-admin) et inscrit au registre ; la page ne fait que demander. Textes en français seulement : seul l'admin la voit.
import { useCallback, useEffect, useState } from 'react';
import { FORGE_MAX_POWER } from '../../../shared/forge';
import { apiGet, apiPost, type MeResponse } from '../game/api';
import { formatNumber } from '../i18n';
import type { Page } from './MenuShell';
import { reqId } from './PaintPage';

interface AdminData {
  balances: Record<string, number>;
  pass: { season: { id: string; name: string }; level: number; premium: boolean; ended: boolean };
  cosmetics: { owned: number; total: number };
  forge: { levels: Record<string, number>; boxes: number; bossBoxes: number };
  grantable: string[];
  grantMax: number;
  log: { action: string; detail: Record<string, unknown>; at: string }[];
}

const LABEL: Record<string, string> = {
  coins: 'Pièces',
  gems: 'Gemmes',
  tok_x2: 'Jetons ×2',
  tok_x5: 'Jetons ×5',
  tok_restore: 'Jetons de restauration',
  tok_reset: 'Jetons de réinitialisation',
  tok_salon: 'Jetons de salon',
  pw_box: 'Coffres de puissance (Forge)',
  pw_box_boss: 'Coffres de boss (Forge)',
};
const label = (c: string) => LABEL[c] ?? (c.startsWith('pw_part_') ? `Pièces détachées : ${c.slice(8)}` : c);

const ACTION: Record<string, string> = {
  grant: 'Don',
  'unlock-pass': 'Pass débloqué',
  'unlock-cosmetics': 'Cosmétiques débloqués',
  'unlock-forge': 'Forge au maximum',
  'game-level': 'En partie : niveau max',
  'game-stats': 'En partie : stats au max',
  'game-god': 'En partie : invincible',
  'game-class': 'En partie : classe',
};

const QUICK: { currency: string; amount: number }[] = [
  { currency: 'coins', amount: 100_000 },
  { currency: 'gems', amount: 10_000 },
  { currency: 'pw_box', amount: 10 },
  { currency: 'pw_box_boss', amount: 5 },
];

export function AdminPage({ me, onChanged, go }: { me: MeResponse | null; onChanged: () => void; go: (p: Page) => void }) {
  const [data, setData] = useState<AdminData | null>(null);
  const [denied, setDenied] = useState(false);
  const [currency, setCurrency] = useState('gems');
  const [amount, setAmount] = useState('1000');
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await apiGet<AdminData>('/api/admin'));
      setDenied(false);
    } catch {
      setDenied(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [me?.account?.publicId, load]);

  if (!me?.account?.admin || denied) {
    return (
      <div className="page admin-page">
        <section className="plate">
          <h1 className="plate-title">Admin</h1>
          <p className="wip">Cette page est réservée au compte admin.</p>
          <button type="button" className="btn" onClick={() => go('hangar')}>
            Retour au hangar
          </button>
        </section>
      </div>
    );
  }

  const grant = async (c: string, n: number) => {
    setBusy(true);
    setMsg(null);
    const r = await apiPost<AdminData & { result: string }>('/api/admin/grant', { currency: c, amount: n, requestId: reqId() });
    setBusy(false);
    if (r.status !== 200 || r.data.result !== 'ok') return setMsg({ text: 'Refusé : vérifie le montant (1 à 1 000 000 000).', ok: false });
    setData(r.data);
    setMsg({ text: `+${formatNumber(n)} ${label(c)}`, ok: true });
    onChanged();
  };

  const unlock = async (what: 'pass' | 'cosmetics' | 'forge') => {
    const r = await apiPost<AdminData & { result: string; granted: number }>('/api/admin/unlock', { what, requestId: reqId() });
    if (r.status === 200) setData(r.data);
    return r.status === 200 ? r.data.result : 'invalid';
  };
  const unlockAll = async (list: ('pass' | 'cosmetics' | 'forge')[]) => {
    setBusy(true);
    setMsg(null);
    const names = { pass: 'pass', cosmetics: 'cosmétiques', forge: 'Forge' };
    const out: string[] = [];
    for (const w of list) {
      const r = await unlock(w);
      out.push(`${names[w]} : ${r === 'ok' ? 'débloqué' : r === 'none' ? 'déjà au max' : r === 'ended' ? 'pas de saison en cours' : 'refusé'}`);
    }
    setBusy(false);
    setMsg({ text: out.join(' · '), ok: true });
    onChanged();
  };

  const n = Number(amount);
  const amountOk = Number.isSafeInteger(n) && n >= 1 && n <= (data?.grantMax ?? 1e9);

  return (
    <div className="page admin-page" data-testid="admin-page">
      <section className="plate" aria-labelledby="admin-title">
        <h1 className="plate-title" id="admin-title">
          Admin
        </h1>
        <p className="wip">Tout ce que tu fais ici va sur ton compte, est inscrit au registre (motif « admin ») et au journal admin. Jamais par Stripe, jamais compté comme un achat.</p>
        {msg && (
          <p className={`form-msg ${msg.ok ? 'ok' : ''}`} role="status" data-testid="admin-msg">
            {msg.text}
          </p>
        )}
      </section>

      {data && (
        <>
          <section className="plate" aria-labelledby="admin-balances">
            <h2 className="plate-title" id="admin-balances">
              Soldes
            </h2>
            <ul className="admin-list" data-testid="admin-balances">
              {data.grantable.map((c) => (
                <li key={c}>
                  <span>{label(c)}</span> <span className="num">{formatNumber(data.balances[c] ?? 0)}</span>
                </li>
              ))}
            </ul>
            <p>
              Pass {data.pass.season.id} : niveau {data.pass.level}, {data.pass.premium ? 'premium' : 'gratuit'} · Cosmétiques : {data.cosmetics.owned} / {data.cosmetics.total} · Forge :{' '}
              {Object.values(data.forge.levels).reduce((s, x) => s + x, 0)} / {FORGE_MAX_POWER}
            </p>
          </section>

          <section className="plate" aria-labelledby="admin-grant">
            <h2 className="plate-title" id="admin-grant">
              Se donner
            </h2>
            <div className="admin-grant">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="admin-currency">
                {data.grantable.map((c) => (
                  <option key={c} value={c}>
                    {label(c)}
                  </option>
                ))}
              </select>
              <input type="number" min={1} max={data.grantMax} step={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="admin-amount" aria-label="Montant" />
              <button type="button" className="btn btn-primary" disabled={busy || !amountOk} onClick={() => void grant(currency, n)} data-testid="admin-give">
                Donner
              </button>
            </div>
            <div className="admin-quick">
              {QUICK.map((q) => (
                <button key={q.currency} type="button" className="btn btn-small" disabled={busy} onClick={() => void grant(q.currency, q.amount)}>
                  +{formatNumber(q.amount)} {label(q.currency)}
                </button>
              ))}
            </div>
          </section>

          <section className="plate" aria-labelledby="admin-unlock">
            <h2 className="plate-title" id="admin-unlock">
              Tout débloquer
            </h2>
            <div className="admin-quick">
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void unlockAll(['pass', 'cosmetics', 'forge'])} data-testid="admin-unlock-all">
                Tout débloquer
              </button>
              <button type="button" className="btn btn-small" disabled={busy} onClick={() => void unlockAll(['pass'])} data-testid="admin-unlock-pass">
                Pass premium niveau max
              </button>
              <button type="button" className="btn btn-small" disabled={busy} onClick={() => void unlockAll(['cosmetics'])} data-testid="admin-unlock-cosmetics">
                Tous les cosmétiques
              </button>
              <button type="button" className="btn btn-small" disabled={busy} onClick={() => void unlockAll(['forge'])} data-testid="admin-unlock-forge">
                Forge au maximum
              </button>
            </div>
            <p className="wip">En partie, le panneau « Admin » (à droite, sous le classement) donne niveau max, stats au max, invincible et classe au choix, dans tous les modes.</p>
          </section>

          <section className="plate" aria-labelledby="admin-log">
            <h2 className="plate-title" id="admin-log">
              Journal
            </h2>
            {data.log.length === 0 ? (
              <p className="wip">Aucune action pour l'instant.</p>
            ) : (
              <ul className="admin-list" data-testid="admin-log">
                {data.log.map((l, i) => (
                  <li key={i}>
                    <span>{new Date(l.at).toLocaleString('fr-FR')}</span> <span>{ACTION[l.action] ?? l.action}</span>{' '}
                    <span className="num">{l.action === 'grant' ? `+${formatNumber(Number(l.detail.amount))} ${label(String(l.detail.currency))}` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
